import { createContext, useContext, useReducer, useEffect } from 'react';

const CartContext = createContext();
const CART_STORAGE_KEY = 'cartState';
const CART_PERSIST_DEBOUNCE_MS = 150;

const initialState = {
  items: [], // {product, qty}
};

const getAvailable = (product) => {
  const raw = product?.inventory ?? product?.stock;
  if (raw === undefined || raw === null) return Infinity;
  const n = Number(raw);
  return Number.isFinite(n) ? Math.max(0, n) : Infinity;
};

const toPersistedProductSnapshot = (product = {}) => {
  const productId = product?._id ?? product?.id;

  return {
    _id: productId,
    id: product.id ?? productId,
    name: product.name,
    price: product.price,
    description: product.description ?? '',
    stock: product.stock,
    inventory: product.inventory,
    imageUrl: product.imageUrl ?? product.image_url ?? product.image ?? null,
    image_url: product.image_url ?? product.imageUrl ?? product.image ?? null,
    image: product.image ?? product.imageUrl ?? product.image_url ?? null,
    images: Array.isArray(product.images) ? product.images.filter(Boolean).slice(0, 5) : [],
  };
};

const normalizePersistedState = (parsed) => {
  if (!parsed || typeof parsed !== 'object' || !Array.isArray(parsed.items)) {
    return initialState;
  }

  return {
    items: parsed.items
      .map((item) => {
        const product = item?.product;
        const productId = product?._id ?? product?.id;

        if (!productId || !Number.isFinite(Number(item?.qty))) {
          return null;
        }

        return {
          qty: Math.max(1, Number(item.qty)),
          product: {
            ...product,
            _id: productId,
            id: product.id ?? productId,
          },
        };
      })
      .filter(Boolean),
  };
};

const serializeCartState = (state) => ({
  items: state.items.map(({ product, qty }) => ({
    qty,
    product: toPersistedProductSnapshot(product),
  })),
});

function reducer(state, action) {
  switch (action.type) {
    case 'ADD': {
      const { product, qty = 1 } = action.payload;
      const available = getAvailable(product);
      if (available <= 0) return state;
      const exists = state.items.find((i) => i.product._id === product._id);
      const items = exists
        ? state.items.map((i) =>
            i.product._id === product._id
              ? { ...i, qty: Math.min(available, i.qty + qty) }
              : i
          )
        : [...state.items, { product, qty: Math.min(available, qty) }];
      return { ...state, items };
    }
    case 'REMOVE':
      return {
        ...state,
        items: state.items.filter((i) => i.product._id !== action.payload),
      };
    case 'UPDATE_QTY': {
      const { id, qty } = action.payload;
      return {
        ...state,
        items: state.items.map((i) => {
          if (i.product._id !== id) return i;
          const available = getAvailable(i.product);
          const nextQty = Math.max(1, Math.min(available === Infinity ? qty : available, qty));
          return { ...i, qty: nextQty };
        }),
      };
    }
    case 'CLEAR':
      // Clear localStorage immediately when clearing cart
      try {
        if (typeof window !== 'undefined') {
          window.localStorage.removeItem('cartState');
        }
      } catch {}
      return initialState;
    case 'SYNC_AVAILABLE': {
      const { id, available } = action.payload;
      return {
        ...state,
        items: state.items.map((i) =>
          i.product._id === id
            ? {
                ...i,
                product: {
                  ...i.product,
                  stock: available,
                  inventory: available,
                },
              }
            : i
        ),
      };
    }
    default:
      return state;
  }
}

export const CartProvider = ({ children }) => {
  const [state, dispatch] = useReducer(
    reducer,
    initialState,
    (baseState) => {
      try {
        if (typeof window === 'undefined') return baseState;
        const stored = window.localStorage.getItem(CART_STORAGE_KEY);
        if (!stored) return baseState;
        return normalizePersistedState(JSON.parse(stored));
      } catch {
        return baseState;
      }
    }
  );

  useEffect(() => {
    let persistTimeoutId;

    try {
      if (typeof window === 'undefined') return;
      persistTimeoutId = window.setTimeout(() => {
        try {
          window.localStorage.setItem(CART_STORAGE_KEY, JSON.stringify(serializeCartState(state)));
        } catch {
        }
      }, CART_PERSIST_DEBOUNCE_MS);
    } catch {
    }

    return () => {
      if (persistTimeoutId) {
        window.clearTimeout(persistTimeoutId);
      }
    };
  }, [state]);
  
  return (
    <CartContext.Provider value={{ state, dispatch }}>{children}</CartContext.Provider>
  );
};

export const useCart = () => useContext(CartContext);

// Utility function to clear all cart-related data
export const clearCartCompletely = () => {
  try {
    if (typeof window === 'undefined') return;
    // Clear cart state from localStorage
    window.localStorage.removeItem('cartState');
    // Clear any other cart-related keys
    window.localStorage.removeItem('activeOrderToken');
    window.localStorage.removeItem('checkoutDelivery');
    window.localStorage.removeItem('deliveryForm');
  } catch (error) {
    console.error('Failed to clear cart data:', error);
  }
};
